import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Leaf, 
  Plus, 
  AlertCircle,
  Check,
  Tags
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LeadStatusModal } from '../components/lead-status/LeadStatusModal';
import { LeadStatusList } from '../components/lead-status/LeadStatusList';

interface LeadStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

export function LeadStatusManagement() {
  const navigate = useNavigate();
  const { userRoles } = useAuth();
  const isAdmin = userRoles.includes('admin');
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<LeadStatus | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/settings');
      return;
    }
    fetchStatuses();
  }, [isAdmin, navigate]);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.rpc('get_lead_statuses');
      
      if (error) throw error;
      
      setStatuses(data || []);
    } catch (error: any) {
      console.error('Error fetching lead statuses:', error);
      setError('Failed to load lead statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSuccess = async () => {
    await fetchStatuses();
    setSuccess(editingStatus ? 'Status updated successfully' : 'Status created successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDelete = async (id: string) => {
    await fetchStatuses();
  };

  // If not admin, don't render the page
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          {/* Header Section */}
          <div className="bg-white shadow-lg rounded-lg mb-6 overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-12">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate('/settings')}
                    className="text-white hover:text-green-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                  <Leaf className="h-12 w-12 text-white opacity-20" />
                </div>
                
                <div className="mt-4 text-center">
                  <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                  <p className="text-green-100 text-lg mb-4">
                    Tracking Health and Reducing Illness Via Efficiency
                  </p>
                  <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                  <h2 className="text-2xl font-semibold text-white mt-4">Lead Status Management</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Tags className="h-5 w-5 text-green-600" />
                  <h3 className="ml-2 text-lg font-medium text-gray-900">
                    Lead Statuses
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setEditingStatus(null);
                    setShowModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Status
                </button>
              </div>
            </div>

            {/* Messages */}
            {error && (
              <div className="px-6 py-4 bg-red-50">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="px-6 py-4 bg-green-50">
                <div className="flex">
                  <Check className="h-5 w-5 text-green-400" />
                  <p className="ml-3 text-sm text-green-700">{success}</p>
                </div>
              </div>
            )}

            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : (
                <LeadStatusList 
                  statuses={statuses}
                  onEdit={(status) => {
                    setEditingStatus(status);
                    setShowModal(true);
                  }}
                  onReorder={fetchStatuses}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <LeadStatusModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStatus(null);
        }}
        onSuccess={handleModalSuccess}
        status={editingStatus}
      />
    </div>
  );
}